/**
 * @file 规范序列化（canonical JSON）。
 *
 * 用途：让「语义相同的对象 → 逐字节相同的字符串」，从而：
 * - `ruleHash` 可比对（config 管线）；
 * - `serializeState()` 的输出在「状态未变时字符串不变」，宿主可据此跳过 DO 写入（省免费额度）。
 *
 * 契约：递归按键的字典序重排、丢弃 `undefined`、数字走 JSON 最短往返表示。
 * 纯函数、无平台依赖（不碰 `Date` / `Math.random` / `crypto`）。
 */

/**
 * 把任意 JSON 安全值规范序列化为稳定字符串。
 *
 * 与 `JSON.stringify` 的区别：对象键**递归按字典序排序**，
 * 且 `undefined` 字段被忽略（而非变成 `null`）。数组顺序保持不变（顺序是语义的一部分）。
 */
export function canonicalStringify(value: unknown): string {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'number' || t === 'boolean') return JSON.stringify(value);
  if (t === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + (value as unknown[]).map((v) => canonicalStringify(v)).join(',') + ']';
  }
  if (t === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return (
      '{' +
      entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalStringify(v)}`).join(',') +
      '}'
    );
  }
  return 'null';
}
